import React from 'react';
import { SvgProps } from 'apps/sigma2-ui/src/components/svg/types';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { IconGradientRightCardSvg } from 'apps/sigma2-ui/src/components/svg/IconGradientRightCard';
import cn from 'classnames';
import { IconGlowSvg } from 'apps/sigma2-ui/src/components/svg/IconGlow';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';

export interface DashboardToolCardProps {
  /**
   * The small icon (SVG) for the card.
   */
  smallIcon: React.FC<SvgProps>;

  /**
   * The large icon for the card (PNG link).
   */
  largeIconSrc: string;

  /**
   * The header of the card.
   */
  headerTranslatedContent: TranslatedContent;

  /**
   * The body of the card.
   */
  bodyTranslatedContent: TranslatedContent;

  /**
   * The color of the glow (shown in the 2-col variant).
   */
  glowColor: string;

  /**
   * The color of the gradient.
   */
  gradientColor: string;

  /**
   * The variant of the card.
   */
  variant: '2-col' | '3-col';

  /**
   * The href for the card.
   */
  href: string;
}

export const DashboardToolCard = ({
  glowColor,
  gradientColor,
  headerTranslatedContent,
  bodyTranslatedContent,
  smallIcon,
  largeIconSrc,
  variant,
  href,
}: DashboardToolCardProps) => {
  return (
    <TouchTarget
      behavior={{
        type: 'href',
        href,
      }}
      content={({ hovered }) => {
        return (
          <span
            className={cn('rounded-lg block transition-all', {
              'shadow-2px': !hovered,
              'shadow-8px': hovered,
              '-translate-y-1': hovered,
            })}
          >
            <Box
              p={'sm'}
              bg={'white'}
              rounded={'lg'}
              h={'h-[200px]'}
              w={'full'}
              position={'relative'}
              as={'span'}
              display={'block'}
              overflow={'hidden'}
            >
              <Spacing
                size={'md'}
                direction={'vertical'}
                as={'span'}
                className={
                  variant === '2-col' ? 'max-w-[300px]' : 'max-w-[192px]'
                }
              >
                <Box
                  w={'w-[34px]'}
                  h={'h-[34px]'}
                  display={'flex'}
                  as={'span'}
                  rounded={'lg'}
                  bg={hovered ? 'royal-200' : 'royal-100'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  color={hovered ? 'royal-800' : 'royal-600'}
                >
                  {smallIcon({
                    svg: {
                      className: 'w-[18px] h-[18px]',
                    },
                  })}
                </Box>

                <S2Typography
                  variant={'headline-6-alt'}
                  translatedContent={headerTranslatedContent}
                  textColorVariant={'gray-900'}
                  as={'span'}
                />

                <S2Typography
                  variant={'body-7'}
                  translatedContent={bodyTranslatedContent}
                  textColorVariant={'gray-800'}
                  as={'span'}
                />
              </Spacing>
              <Box
                position={'absolute'}
                right={'0'}
                top={'0'}
                h={'full'}
                w={variant === '2-col' ? 'w-[268px]' : 'w-[148px]'}
                as={'span'}
              >
                <IconGradientRightCardSvg
                  svg={{
                    style: {
                      position: 'absolute',
                      right: 0,
                      top: 0,
                    },
                  }}
                  wantsColor={gradientColor}
                />

                <img
                  src={largeIconSrc}
                  className={cn('absolute', {
                    'w-[154px] h-[154px] left-[40px] top-[23px]':
                      variant === '2-col',
                    'w-[118px] h-[118px] left-[5px] top-[40px]':
                      variant === '3-col',
                  })}
                  alt={getTranslatedContentAsString(headerTranslatedContent)}
                />

                <IconGlowSvg
                  wantsColor={glowColor}
                  svg={{
                    style: {
                      position: 'absolute',
                      left: variant === '2-col' ? '35px' : '0px',
                      top: '161px',
                    },
                  }}
                />
              </Box>
            </Box>
          </span>
        );
      }}
    />
  );
};
